import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnDestroy,
  output,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  combineLatest,
  filter,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';

import { DatasetInfo } from '@blaze-shared/api-contracts-domain';
import {
  ConnectorsApiService,
  ConnectorsCollectionService,
} from '@blaze-shared/connectors-data-access';
import {
  type ConnectorConfiguration,
  type ConnectorTemplate,
} from '@blaze-shared/connectors-domain';
import { TablesApiService, TablesCollectionService } from '@blaze-shared/datasets-data-access';
import { type LinkedBlazeTableConnector } from '@blaze-shared/datasets-domain';
import { LocalStorageService, StorageKey } from '@blaze-shared/platform-data-access';
import { DialogService, EnvironmentIndicatorComponent } from '@blaze-shared/shared-feature';
import {
  ButtonBarComponent,
  FilterInputComponent,
  PanelComponent,
  PanelRef,
  safeDetectChanges,
} from '@blaze-shared/shared-ui';

import { DataConnectorsStore } from '../+state/data-connectors.store';
import { DatasetToggleComponent } from '../dataset-toggle/dataset-toggle.component';
import { prepareEditedConnectorConfiguration } from '../utils';

@Component({
  selector: 'blaze-data-connector-datasets',
  imports: [
    AsyncPipe,
    ButtonBarComponent,
    DatasetToggleComponent,
    EnvironmentIndicatorComponent,
    FilterInputComponent,
    FormsModule,
    MatIconModule,
    MatSlideToggleModule,
    PanelComponent,
    ReactiveFormsModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './data-connector-datasets.component.html',
  styleUrls: ['./data-connector-datasets.component.scss'],
  host: { class: 'data-connector-dialog' },
})
export class DataConnectorDatasetsComponent implements OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly connectorsApiService = inject(ConnectorsApiService);
  private readonly connectorsCollService = inject(ConnectorsCollectionService);
  private readonly dataConnectorsStore = inject(DataConnectorsStore);
  private readonly dialogService = inject(DialogService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly panelRef = inject(PanelRef);
  private readonly tablesApiService = inject(TablesApiService);
  private readonly tablesCollService = inject(TablesCollectionService);

  showPreview = output<DatasetInfo>();
  showPrevPage = output();

  @Input() connector?: ConnectorConfiguration;
  @Input() linkedTableConnector?: LinkedBlazeTableConnector;
  @Input() template?: ConnectorTemplate;

  datasets$!: Observable<DatasetInfo[]>;
  areAllSelected = false;
  selectedDatasets = new Set<string>();
  subSelectAll!: Subscription;
  availableDatasets$ = this.dataConnectorsStore.availableDatasets$;
  initialSelectionEmpty: boolean | undefined = undefined;
  addDatasetsButtonLabel = 'add data sets';

  get connectorName(): string {
    if (this.linkedTableConnector) {
      return this.linkedTableConnector.projectName;
    }
    return this.connector?.id
      ? `${this.connector.name} / ${this.template?.displayName || this.connector.target}`
      : `${this.template?.target}`;
  }

  get isCreateButtonDisabled(): boolean {
    return (this.initialSelectionEmpty ?? true) && this.selectedDatasets.size === 0;
  }

  /**
   * Create connector button should only be visible when creating a new connector (and not editing existing one).
   * @returns
   */
  get isCreateButtonVisible(): boolean {
    return !this.connector?.id && !this.linkedTableConnector;
  }

  /**
   * Update connector button should only be visible when editing a new connector (and not creating a new one).
   * @returns
   */
  get isUpdateButtonVisible(): boolean {
    return !!this.connector?.id;
  }

  get isAddDatasetsButtonVisible(): boolean {
    return !!this.linkedTableConnector;
  }

  constructor() {
    this.init();
  }

  ngOnDestroy(): void {
    this.subSelectAll?.unsubscribe();
  }

  addDatasets(): void {
    const targetProjectId = this.linkedTableConnector?.projectId;
    if (!targetProjectId) {
      throw new Error('Add Datasets - Target project not found');
    }
    combineLatest([this.dataConnectorsStore.projectId$, this.dataConnectorsStore.selectedDatasets$])
      .pipe(
        take(1),
        switchMap(([projectId, selectedDatasets]) => {
          const tableIds = selectedDatasets.map((d) => d.id).filter((id): id is string => !!id);
          return this.tablesApiService
            .postLinkProjectTables(projectId, targetProjectId, tableIds)
            .pipe(
              withLatestFrom(this.tablesCollService.entities$),
              map(([linkedTables, allTables]) => {
                const tablesToRemove = allTables.filter(
                  (table) => table.projectId === targetProjectId,
                );
                return { linkedTables, tablesToRemove };
              }),
            );
        }),
      )
      .subscribe(({ linkedTables, tablesToRemove }) => {
        this.tablesCollService.removeManyFromCache(tablesToRemove);
        this.tablesCollService.upsertManyInCache(linkedTables);
        this.closeDialog();
      });
  }

  closeDialog(): void {
    this.panelRef.close();
  }

  /**
   * Creates new connection for the account.
   */
  createConnector(): void {
    combineLatest([
      this.dataConnectorsStore.formValues$,
      this.dataConnectorsStore.selectedDatasets$,
    ])
      .pipe(
        take(1),
        filter(([_formValues, selectedDatasets]) => !!selectedDatasets.length),
        map(([formValues, selectedDatasets]) => {
          const config: ConnectorConfiguration = {
            accountId: this.localStorageService.get(StorageKey.AccountId) || '',
            actions: [],
            configuration: { ...formValues },
            datasets: selectedDatasets,
            name: formValues?.['name'] as string,
            templateId: this.template?.id || this.connector?.templateId,
          };
          return config;
        }),
        switchMap((connectorConfig) => this.connectorsCollService.add(connectorConfig)),
        filter((response) => !!response?.id),
      )
      .subscribe(() => {
        this.closeDialog();
      });
  }

  deleteConnector(): void {
    console.log('Delete?: ', this.connector);
    if (confirm(`Do you want to delete this connector?`)) {
      this.connectorsCollService.delete(this.connector?.id || '');
      this.closeDialog();
    }
  }

  updateConnector(): void {
    combineLatest({
      coupledConnector: this.dataConnectorsStore.coupledConnector$,
      selectedDatasets: this.dataConnectorsStore.selectedDatasets$,
    })
      .pipe(
        take(1),
        switchMap(({ coupledConnector, selectedDatasets }) => {
          if (!coupledConnector) {
            return of(true);
          }

          const areSelectedDatasetsEqual =
            selectedDatasets.length === coupledConnector.datasets.length &&
            selectedDatasets.every((d) => coupledConnector.datasets.some((cd) => cd.id === d.id));
          if (areSelectedDatasetsEqual) {
            return of(true);
          }

          return this.dialogService
            .openConfirmDialog({
              title: 'Datasets mismatch',
              confirmLabel: 'OK',
              cancelLabel: 'Cancel',
              description:
                'Selected datasets are different between multiple environments.<br />Updating connector will change datasets for every environment.<br />Do you want to proceed?',
            })
            .afterClosed();
        }),
        filter(Boolean),
        switchMap(() =>
          combineLatest([
            this.dataConnectorsStore.selectedDatasets$,
            this.dataConnectorsStore.formConfig$,
            this.dataConnectorsStore.formValues$,
            this.dataConnectorsStore.connectorConfigChanged$,
          ]).pipe(
            take(1),
            filter(([selectedDatasets]) => !!selectedDatasets.length && !!this.connector?.id),
            switchMap(([selectedDatasets, formConfig, formValues, connectorConfigChanged]) => {
              const connectorId = this.connector?.id || '';
              if (connectorConfigChanged) {
                const config = prepareEditedConnectorConfiguration(
                  formConfig,
                  formValues,
                  selectedDatasets,
                );
                return this.connectorsApiService.putUpdateConnector(connectorId, config);
              }
              return this.connectorsApiService.postUpdateConnectorDatasets(
                connectorId,
                selectedDatasets,
              );
            }),
            filter((response) => !!response?.id),
          ),
        ),
      )
      .subscribe((response) => {
        this.connectorsCollService.updateOneInCache(response);
        this.closeDialog();
      });
  }

  isSelected(dataset: DatasetInfo): boolean {
    return this.selectedDatasets.has(dataset?.name);
  }

  selectDeselectAll(): void {
    if (!this.areAllSelected) {
      // Deselect all
      this.dataConnectorsStore.setSelectedDatasets([]);
    } else {
      // Select all
      this.dataConnectorsStore.availableDatasets$.pipe(take(1)).subscribe((available) => {
        this.dataConnectorsStore.setSelectedDatasets([...available]);
      });
    }
  }

  setFilteredDatasets(datasets$: Observable<DatasetInfo[]>): void {
    this.datasets$ = datasets$;
  }

  updateSelectedToggles(isSelected: boolean, dataset: DatasetInfo): void {
    if (!isSelected) {
      // Remove selected dataset
      this.dataConnectorsStore.deleteSelectedDataset(dataset);
    } else {
      // Add new selected dataset
      this.dataConnectorsStore.addSelectedDataset(dataset);
    }
  }

  /**
   * Subscribes to store changes to properly initialize used params.
   */
  private init(): void {
    this.subSelectAll = combineLatest([
      this.dataConnectorsStore.availableDatasets$,
      this.dataConnectorsStore.selectedDatasets$,
    ]).subscribe(([available, selected]) => {
      const selectedNames = selected.map((dataset) => dataset.name);
      this.selectedDatasets = new Set(selectedNames);
      if (this.initialSelectionEmpty === undefined) {
        this.initialSelectionEmpty = this.selectedDatasets.size === 0;
        if (!this.initialSelectionEmpty) {
          this.addDatasetsButtonLabel = 'update data sets';
        }
      }
      this.areAllSelected = available.length === selected.length;
      safeDetectChanges(this.cdr);
    });
  }
}
